import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  AdminLayout,
  PageHeader,
  Card,
  Button,
  StatCard,
  ActivityItem,
  Badge,
  LoadingSpinner,
  ErrorDisplay,
  useToast,
} from "./components/AdminSharedComponents";
import { Eye, BarChart } from "lucide-react";
import VisitorDashboard from "../admin/components/VisitorDashboard";
import useVisitorCounter from "../../../hooks/UseVisitorCounter";
import ReportModal from "./report/ReportModal";
import api from "../../../utils/api";

// Mapping untuk aksi project, product, dan report
const actionMapping = {
  CREATE_REPORT: {
    title: "Laporan Dibuat",
    badge: { text: "Laporan", variant: "primary" },
  },
  UPDATE_REPORT: {
    title: "Laporan Diperbarui",
    badge: { text: "Laporan", variant: "primary" },
  },
  DELETE_REPORT: {
    title: "Laporan Dihapus",
    badge: { text: "Laporan", variant: "danger" },
  },
  CREATE_PROJECT: {
    title: "Proyek Dibuat",
    badge: { text: "Proyek", variant: "success" },
  },
  UPDATE_PROJECT: {
    title: "Proyek Diperbarui",
    badge: { text: "Proyek", variant: "success" },
  },
  DELETE_PROJECT: {
    title: "Proyek Dihapus",
    badge: { text: "Proyek", variant: "danger" },
  },
  CREATE_PRODUCT: {
    title: "Produk Dibuat",
    badge: { text: "Produk", variant: "info" },
  },
  UPDATE_PRODUCT: {
    title: "Produk Diperbarui",
    badge: { text: "Produk", variant: "info" },
  },
  DELETE_PRODUCT: {
    title: "Produk Dihapus",
    badge: { text: "Produk", variant: "danger" },
  },
  PARTIAL_UPDATE_PRODUCT: {
    title: "Produk Diperbarui Sebagian",
    badge: { text: "Produk", variant: "info" },
  },
};

// Fungsi untuk menghitung waktu "ago"
const calculateTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} hari lalu`;
  if (hours > 0) return `${hours} jam lalu`;
  if (minutes > 0) return `${minutes} menit lalu`;
  return "baru saja";
};

// Fungsi untuk mendapatkan role user dari token atau localStorage
const getUserRole = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;

    // Decode JWT token untuk mendapatkan role
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.role;
  } catch (error) {
    console.error("Error decoding token:", error);
    // Fallback: cek dari localStorage langsung
    return localStorage.getItem("userRole");
  }
};

// Fungsi untuk mengecek apakah user memiliki akses admin
const hasAdminAccess = (userRole) => {
  return userRole === "admin" || userRole === "admin-proyek";
};

export const Dashboard = () => {
  const visitorData = useVisitorCounter("/admin/dashboard");
  const toast = useToast();
  const toastRef = useRef(toast);

  // State untuk role user
  const [userRole, setUserRole] = useState(null);

  // State utama dashboard
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  // State untuk aktivitas
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState(null);

  // State untuk modal laporan
  const [selectedReport, setSelectedReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fungsi untuk mencatat aktivitas baru
  const logNewActivity = async (userId, action, description, metadata = {}) => {
    try {
      const response = await api.post(
        "/api/activities/log",
        {
          userId,
          action,
          description,
          metadata,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Aktivitas berhasil dicatat");
      // Refresh aktivitas setelah mencatat
      fetchRecentActivities();
      return response.data;
    } catch (err) {
      console.error("Error logging activity:", err);
      const errorMessage =
        err.response?.data?.message || "Gagal mencatat aktivitas";
      toast.error(errorMessage);
      throw err;
    }
  };

  // Fungsi untuk mengambil aktivitas terbaru
  const fetchRecentActivities = useCallback(async (limit = 5) => {
    try {
      setActivitiesLoading(true);
      setActivitiesError(null);
      const response = await api.get(`/api/activities/recent?limit=${limit}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setActivities(response.data.data || []);
    } catch (err) {
      console.error("Error fetching recent activities:", err);
      const errorMessage =
        err.response?.data?.message || "Gagal memuat aktivitas terbaru";
      setActivitiesError(errorMessage);
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }, []);

  // Fungsi untuk mengambil laporan terbaru
  const fetchRecentReports = useCallback(async (limit = 5) => {
    try {
      setReportsLoading(true);
      setReportsError(null);
      const response = await api.get(
        `/api/reports?sort=created_at&order=desc&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Handle response data structure
      const responseData = response.data;
      if (responseData.data) {
        setReports(responseData.data || []);
      } else {
        // Fallback for different API response structure
        setReports(responseData || []);
      }
    } catch (err) {
      console.error("Error fetching recent reports:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Gagal memuat laporan terbaru";
      setReportsError(errorMessage);
      setReports([]);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  // Fungsi untuk mengambil proyek terbaru
  const fetchRecentProjects = useCallback(async (limit = 5) => {
    try {
      setProjectsLoading(true);
      setProjectsError(null);
      const response = await api.get(
        `/api/projects?sort=created_at&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Handle response data structure
      const responseData = response.data;
      if (responseData.data) {
        setProjects(responseData.data || []);
      } else {
        // Fallback for different API response structure
        setProjects(responseData || []);
      }
    } catch (err) {
      console.error("Error fetching recent projects:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Gagal memuat proyek terbaru";
      setProjectsError(errorMessage);
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  // Inisialisasi dashboard
  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        // Set user role
        const role = getUserRole();
        setUserRole(role);

        setTimeout(() => {
          setStats({
            totalProjects: 24,
            activeEmployees: 12,
            totalClients: 8,
            revenue: "Rp 45.000.000",
          });
          setLoading(false);
        }, 1000);                                                                                                               
        await Promise.all([
          fetchRecentReports(5),
          fetchRecentProjects(5),
          fetchRecentActivities(5),
        ]);
      } catch (err) {
        console.error("Dashboard initialization error:", err);
        setError("Terjadi kesalahan saat memuat dashboard");
        setLoading(false);
      }
    };

    initializeDashboard();
  }, [fetchRecentReports, fetchRecentProjects, fetchRecentActivities]);

  // Update toast ref when toast changes
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  // Handle error states with toast messages
  useEffect(() => {
    if (reportsError) {
      toastRef.current.error("Gagal memuat laporan terbaru");
    }
  }, [reportsError]);

  useEffect(() => {
    if (projectsError) {
      toastRef.current.error("Gagal memuat proyek terbaru");
    }
  }, [projectsError]);

  useEffect(() => {
    if (activitiesError) {
      toastRef.current.error("Gagal memuat aktivitas terbaru");
    }
  }, [activitiesError]);

  // Fungsi untuk membuka modal laporan
  const openReportModal = (report) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  // Fungsi untuk navigasi ke detail project
  const handleProjectClick = (project) => {
    // Navigate to project detail page
    window.location.href = `/admin/projects/show/${project.id}`;
  };

  // Fungsi refresh
  const refreshReports = () => fetchRecentReports(5);
  const refreshProjects = () => fetchRecentProjects(5);
  const refreshActivities = () => fetchRecentActivities(5);

  // Fungsi untuk testing pencatatan aktivitas
  const handleTestLog = () => {
    logNewActivity(
      1, // userId
      "CREATE_PROJECT",
      "Membuat proyek baru: Project X",
      { projectId: 123, projectName: "Project X" }
    );
  };

  // Check if user has admin access
  const canAccessAdminFeatures = hasAdminAccess(userRole);

  // Tampilkan loading jika dashboard utama sedang dimuat
  if (loading) return <LoadingSpinner message="Memuat dashboard..." />;
  if (error) return <ErrorDisplay error={error} />;

  return (
    <AdminLayout>
      <PageHeader
        title="Admin Dashboard"
        subtitle="Overview of key metrics and activities"
      />

      {/* Bagian Pengunjung Website */}
      <Card title="Website Visitors" className="mb-6">
        {visitorData ? (
          <VisitorDashboard />
        ) : (
          <LoadingSpinner message="Memuat data pengunjung..." />
        )}
      </Card>

      {/* Ikhtisar Statistik */}
      <div className="mb-6">
        <StatCard
          title="Total Proyek"
          value={stats.totalProjects}
          icon={BarChart}
          change="+4 sejak bulan lalu"
          changeType="positive"
        />
      </div>

      {/* Laporan Terbaru dan Proyek Terbaru */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <Card
            title="Recent Reports"
            headerAction={
              canAccessAdminFeatures && (
                <Button
                  variant="primary"
                  onClick={() => (window.location.href = "/admin/reports")}
                  className="px-4 py-2 text-sm"
                >
                  Lihat Semua Laporan
                </Button>
              )
            }
          >
            {reportsLoading ? (
              <LoadingSpinner message="Memuat laporan..." />
            ) : reportsError ? (
              <div>
                <ErrorDisplay error={reportsError} />
                <div className="mt-4 text-center">
                  <Button variant="secondary" onClick={refreshReports}>
                    Coba Lagi
                  </Button>
                </div>
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">Tidak ada laporan terbaru</p>
                <Button variant="secondary" onClick={refreshReports}>
                  Refresh
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {reports.map((report) => (
                  <div
                    key={report.id}
                    className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors border border-gray-200 hover:border-primary/30"
                    onClick={() => openReportModal(report)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                          {report.title}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="ml-3">
                        <Eye className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div>
          <Card
            title="Recent Projects"
            headerAction={
              canAccessAdminFeatures && (
                <Button
                  variant="primary"
                  onClick={() => (window.location.href = "/admin/projects")}
                  className="px-4 py-2 text-sm"
                >
                  Lihat Semua
                </Button>
              )
            }
          >
            {projectsLoading ? (
              <LoadingSpinner message="Memuat proyek..." />
            ) : projectsError ? (
              <div>
                <ErrorDisplay error={projectsError} />
                <div className="mt-4 text-center">
                  <Button variant="secondary" onClick={refreshProjects}>
                    Coba Lagi
                  </Button>
                </div>
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">Tidak ada proyek terbaru</p>
                <Button variant="secondary" onClick={refreshProjects}>
                  Refresh
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors border border-gray-200 hover:border-primary/30"
                    onClick={() => handleProjectClick(project)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                          {project.name}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {project.description || "No description available"}
                        </p>
                      </div>
                      <div className="ml-3 flex items-center gap-2">
                        <Badge
                          variant={
                            project.status === "Completed"
                              ? "success"
                              : "primary"
                          }
                        >
                          {project.status}
                        </Badge>
                        <Eye className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Bagian Aktivitas */}
      {/* <div className="mb-6">
        <Card title="Recent Activity">
          {activitiesLoading ? (
            <LoadingSpinner message="Memuat aktivitas..." />
          ) : activitiesError ? (
            <div>
              <ErrorDisplay error={activitiesError} />
              <div className="mt-4 text-center">
                <Button variant="secondary" onClick={refreshActivities}>
                  Coba Lagi
                </Button>
              </div>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">Tidak ada aktivitas terbaru</p>
              <Button variant="secondary" onClick={refreshActivities}>
                Refresh
              </Button>
            </div>
          ) : (
            <div className="space-y-0">
              {activities.slice(0, 5).map((activity) => {
                const mapping = actionMapping[activity.action] || {
                  title: activity.action,
                  badge: { text: "Aktivitas", variant: "secondary" },
                };
                const time = calculateTimeAgo(activity.created_at);
                return (
                  <ActivityItem
                    key={activity.id}
                    title={mapping.title}
                    description={activity.description}
                    time={time}
                    badge={mapping.badge}
                  />
                );
              })}
            </div>
          )}
          <div className="mt-4 text-center">
            {canAccessAdminFeatures && (
              <Button
                variant="text"
                onClick={() => (window.location.href = "/admin/activities")}
              >
                Lihat Semua Aktivitas
              </Button>
            )}
            {canAccessAdminFeatures && (
              <Button
                variant="primary"
                onClick={handleTestLog}
                className="ml-4"
              >
                Catat Aktivitas Contoh
              </Button>
            )}
          </div>
        </Card>
      </div> */}

      {/* Report Modal */}
      {isModalOpen && selectedReport && (
        <ReportModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          report={selectedReport}
          project={projects.find((p) => p.id === selectedReport.project_id)}
        />
      )}
    </AdminLayout>
  );
};

export default Dashboard;
